import {
  CancelOutlined,
  CheckCircleOutlined,
  TaskAltOutlined,
  ThermostatOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { alpha, Box, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ChecklistData {
  temperature: number | null;
  vehicleHygiene: boolean;
  packagingIntact: boolean;
  notes: string | null;
}

interface ComplianceSummaryProps {
  checklist: ChecklistData;
}

export function ComplianceSummary({ checklist }: ComplianceSummaryProps) {
  const { t } = useTranslation('goodsReceiving');
  const allCompliant = checklist.vehicleHygiene && checklist.packagingIntact;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: 1,
        borderColor: allCompliant ? 'divider' : alpha('#DC2626', 0.4),
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        sx={{
          alignItems: 'center',
          px: 2,
          py: 1.5,
          bgcolor: allCompliant ? alpha('#059669', 0.08) : alpha('#DC2626', 0.08),
        }}
      >
        {allCompliant ? (
          <TaskAltOutlined sx={{ color: 'success.main', flexShrink: 0 }} />
        ) : (
          <WarningAmberOutlined sx={{ color: 'error.main', flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: allCompliant ? 'success.dark' : 'error.dark' }}
          >
            {allCompliant ? t('checksAllCompliant') : t('checksNotCompliant')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('checklistSectionTitle')}
          </Typography>
        </Box>
      </Stack>

      <Stack sx={{ px: 2, py: 0.5 }} divider={<Divider />}>
        <CheckRow label={t('checklistVehicleHygiene')} isCompliant={checklist.vehicleHygiene} />
        <CheckRow label={t('checklistPackaging')} isCompliant={checklist.packagingIntact} />
        {checklist.temperature !== null && (
          <ValueRow
            icon={<ThermostatOutlined sx={{ fontSize: 18 }} />}
            label={t('temperature')}
            value={`${checklist.temperature}°C`}
          />
        )}
      </Stack>

      {checklist.notes && (
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}
            >
              {t('notes')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {checklist.notes}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function CheckRow({ label, isCompliant }: { label: string; isCompliant: boolean }) {
  const { t } = useTranslation('goodsReceiving');
  const color = isCompliant ? 'success.main' : 'error.main';

  return (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', py: 1.25 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color }}>
        {isCompliant ? (
          <CheckCircleOutlined sx={{ fontSize: 18 }} />
        ) : (
          <CancelOutlined sx={{ fontSize: 18 }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {isCompliant ? t('compliantLabel') : t('nonCompliantLabel')}
        </Typography>
      </Stack>
    </Stack>
  );
}

function ValueRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', py: 1.25 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.primary' }}>
        {icon}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Stack>
    </Stack>
  );
}
